// FORGE pipeline data hooks — polling hooks for all /api/forge/* endpoints.
// Default interval is 120s (non-critical, infrequently updated pipeline data).

import { usePolling } from "./useDashboard";

// ── Shared wrapper ────────────────────────────────────────────────────────────

/**
 * All FORGE API endpoints return { data: T[], count: number }.
 */
export interface ForgeResponse<T> {
  data: T[];
  count: number;
}

// ── Row interfaces ────────────────────────────────────────────────────────────

/** dash_membership_changes — sourced from airtable_ME_Membership_Changes */
export interface MembershipChangeRow {
  airtable_id: string;
  airtable_created_time?: string | null;
  effective_date?: string | null;
  new_status?: string | null;
  previous_status?: string | null;
  created_by?: string | null;
  full_name_co_id?: string | null;
  change?: string | null;
  status?: string | null;
  attrition_cause?: string | null;
  los_active_months?: number | string | null;
  current_status_in_members?: string | null;
  email_department_from_full_name_co_id?: string | null;
  current_status?: string | null;
  synced_at?: string | null;
  [key: string]: unknown;
}

/** dash_recruit_training — sourced from podio Recruit Training */
export interface RecruitTrainingRow {
  airtable_id: string;
  status?: string | null;
  kpi_days_in_recruit_training?: number | string | null;
  synced_at?: string | null;
  [key: string]: unknown;
}

/**
 * ── CLOSED FEED SHAPES ───────────────────────────────────────────────────────
 *
 * The four row types below are the feeds MemberEngagement joins on. They are
 * CLOSED — no `[key: string]: unknown` — and list exactly the keys the handler
 * puts on the wire: the live D1 columns, minus what `lib/pii.ts` drops,
 * snake_cased by `normalizeRow` in `forge-api.ts`. Verified against remote D1
 * (sqlite_master + pragma_table_info) on 2026-08-27 and asserted end-to-end in
 * `pages/MemberEngagement.wire.test.ts`.
 *
 * WHY CLOSED. An index signature types `row.anything` as `unknown`, and
 * `unknown` is accepted by every `asString(v: unknown)` reader in this
 * codebase. That is not a weak type, it is no type: it is what let
 * MemberEngagement read `m.status` off dash_members for months, and it is what
 * would have let these four feeds be read with the wrong key just as silently.
 * `MemberRow` was closed for exactly this reason; leaving these open would have
 * moved the loophole one file over rather than removing it.
 *
 * If you need a column not listed here, confirm it with
 * `pragma_table_info('<table>')` against remote D1 AND confirm `lib/pii.ts`
 * does not drop it. Genuinely dynamic access still works via an explicit
 * `Record<string, unknown>` cast, which keeps the unchecked read visible in the
 * diff.
 */

/**
 * dash_physicals — 17 live columns, none PII-dropped.
 *
 * `member` is the member's 17-char Airtable record id (`rec…`), NOT a name —
 * it joins to `MemberRow.airtable_id`. `days_until_expired` is TEXT of the form
 * "87 Days" / "-536 Days" / "NaN Days", and `expires` can be the Airtable error
 * blob `{"error":"#ERROR"}` (10 live rows). Neither is a number or a date.
 */
export interface PhysicalsRow {
  airtable_id: string;
  airtable_created_time?: string | null;
  member_id?: string | null;
  id2?: number | null;
  /** The member's airtable_id. The join key. */
  member?: string | null;
  scheduled?: string | null;
  status?: string | null;
  urs_track?: string | null;
  expires?: string | null;
  /** TEXT, e.g. "87 Days". Parsed with parseFloat, never trusted as a number. */
  days_until_expired?: string | null;
  /** 6-char CO_ID. NOT a member key — it cannot equal an airtable_id. */
  co_id_from_member?: string | null;
  dfr_reporting?: string | null;
  variance?: string | null;
  status_new_from_member?: string | null;
  completed?: string | null;
  clearance_received?: string | null;
  synced_at?: string | null;
}

/**
 * dash_meeting_attendance — 16 live columns, 6 dropped as PII
 * (email_personal_from_member, cell_from_member, zip_from_member,
 * state_from_member, city_from_member, street_address_from_member), 10 on the
 * wire. Those six were previously DECLARED here and have never been sent; they
 * are removed as phantoms.
 *
 * `member` is the member's airtable_id. `name_co_id` is a MEETING label that
 * begins with a date ("10/14/2025 …") — it is not a member key.
 */
export interface MeetingAttendanceRow {
  airtable_id: string;
  airtable_created_time?: string | null;
  /** Meeting label, e.g. "10/14/2025 General Meeting". NOT a member key. */
  name_co_id?: string | null;
  /** The member's airtable_id. The join key. */
  member?: string | null;
  status_new_from_member?: string | null;
  preferred_name_from_member?: string | null;
  meeting_date?: string | null;
  created_by?: string | null;
  notes_to_membership?: string | null;
  synced_at?: string | null;
}

/**
 * dash_driver_training — 21 live columns, 7 dropped as PII (age, email, phone,
 * drivers_license, drivers_license_state, drivers_license_exp_date,
 * birth_date), 14 on the wire.
 *
 * `member` is the member's airtable_id. `training_request_for` is an APPARATUS
 * type ("Ambulance", "Engine", "Tower", "Brush") — it is not a person and was
 * never a usable fallback key. `assigned_preceptor` was declared here and does
 * not exist in the table; removed as a phantom.
 */
export interface DriverTrainingRow {
  airtable_id: string;
  airtable_created_time?: string | null;
  idnum?: number | null;
  id?: string | null;
  /** Apparatus type, e.g. "Ambulance". NOT a member key. */
  training_request_for?: string | null;
  approval?: string | null;
  packet_completed?: string | null;
  dept_id?: number | null;
  assigned_station?: string | null;
  battalion?: string | null;
  /** The member's airtable_id. The join key. */
  member?: string | null;
  currently_turned_over_to_drive_on?: string | null;
  reason_for_denial?: string | null;
  synced_at?: string | null;
}

/**
 * dash_losap — 7 live columns, none PII-dropped.
 *
 * `name` is the "Name (CO_ID)" composite, which joins to
 * `MemberRow.full_name_co_id` — NOT to `full_name`. Live: only Year 2023 rows
 * exist, so any current-year figure derived from this feed is over absent data.
 */
export interface LosapRow {
  airtable_id: string;
  airtable_created_time?: string | null;
  /** "Name (CO_ID)" composite. Joins to full_name_co_id, not full_name. */
  name?: string | null;
  year?: string | null;
  status?: string | null;
  membership_changes?: string | null;
  synced_at?: string | null;
}

/** Retention cohort row — selected subset of dash_members */
export interface RetentionCohortRow {
  joined_date?: string | null;
  separation_date?: string | null;
  status_new?: string | null;
  [key: string]: unknown;
}

/** At-risk member row — selected subset of dash_members for active members */
export interface AtRiskRow {
  full_name?: string | null;
  status_new?: string | null;
  joined_date?: string | null;
  inactive_months?: number | null;
  t_2024_co_meeting_attendance?: number | null;
  t_2023_co_meeting_attendance?: number | null;
  meeting_attendance_ytd?: number | null;
  meeting_attendance_last_year?: number | null;
  meeting_attendance_pct_ytd?: number | null;
  meeting_attendance_pct_last_year?: number | null;
  [key: string]: unknown;
}

/** dash_applicant_comments — sourced from ApplicantsComments */
export interface ApplicantCommentRow {
  airtable_id?: string | null;
  parent?: string | null;
  text?: string | null;
  author_id?: string | null;
  author_name?: string | null;
  author_email?: string | null;
  mentioned?: string | null;
  internal_created_date?: string | null;
  internal_updated_date?: string | null;
  synced_at?: string | null;
  [key: string]: unknown;
}

// ── useMembershipChanges ──────────────────────────────────────────────────────

/**
 * Fetches GET /api/forge/membership-changes?limit=2000 and polls every 2 min.
 * Returns all membership status change records.
 */
export function useMembershipChanges(intervalMs = 120_000) {
  return usePolling<ForgeResponse<MembershipChangeRow>>(
    "/api/forge/membership-changes?limit=2000",
    intervalMs,
  );
}

// ── useRecruitTraining ────────────────────────────────────────────────────────

/**
 * Fetches GET /api/forge/recruit-training and polls every 2 min.
 * Returns current recruit training records.
 */
export function useRecruitTraining(intervalMs = 120_000) {
  return usePolling<ForgeResponse<RecruitTrainingRow>>(
    "/api/forge/recruit-training",
    intervalMs,
  );
}

// ── usePhysicals ──────────────────────────────────────────────────────────────

/**
 * Fetches GET /api/forge/physicals and polls every 2 min.
 * Returns member physical exam records ordered by expiration date ascending.
 */
export function usePhysicals(intervalMs = 120_000) {
  return usePolling<ForgeResponse<PhysicalsRow>>(
    "/api/forge/physicals",
    intervalMs,
  );
}

// ── useMeetingAttendance ──────────────────────────────────────────────────────

interface MeetingAttendanceParams {
  since?: string;
  member?: string;
  limit?: number;
}

/**
 * Fetches GET /api/forge/meeting-attendance and polls every 2 min.
 * Defaults to year-start and a 5000-row limit for full-year analysis.
 */
export function useMeetingAttendance(
  params?: MeetingAttendanceParams,
  intervalMs = 120_000,
) {
  const yearStart = `${new Date().getFullYear()}-01-01`;
  const qs = new URLSearchParams();
  qs.set("since", params?.since ?? yearStart);
  qs.set("limit", String(params?.limit ?? 5000));
  if (params?.member) qs.set("member", params.member);

  return usePolling<ForgeResponse<MeetingAttendanceRow>>(
    `/api/forge/meeting-attendance?${qs.toString()}`,
    intervalMs,
  );
}

// ── useRetentionCohorts ───────────────────────────────────────────────────────

/**
 * Fetches GET /api/forge/retention-cohorts and polls every 2 min.
 * Returns joined_date, separation_date, and status_new for all members
 * with a known join date — used to compute retention/survival curves.
 */
export function useRetentionCohorts(intervalMs = 120_000) {
  return usePolling<ForgeResponse<RetentionCohortRow>>(
    "/api/forge/retention-cohorts",
    intervalMs,
  );
}

// ── useAtRisk ─────────────────────────────────────────────────────────────────

/**
 * Fetches GET /api/forge/at-risk and polls every 2 min.
 * Returns active members ranked by inactivity risk (inactive_months DESC).
 */
export function useAtRisk(intervalMs = 120_000) {
  return usePolling<ForgeResponse<AtRiskRow>>("/api/forge/at-risk", intervalMs);
}

// ── useApplicantComments ──────────────────────────────────────────────────────

/**
 * Fetches GET /api/forge/applicant-comments?limit=1000 and polls every 2 min.
 * Returns applicant comment records, newest first.
 */
export function useApplicantComments(intervalMs = 120_000) {
  return usePolling<ForgeResponse<ApplicantCommentRow>>(
    "/api/forge/applicant-comments?limit=1000",
    intervalMs,
  );
}

// ── usePipelineWaterfall ──────────────────────────────────────────────────────

/** Single step row returned by /api/forge/pipeline-waterfall */
export interface WaterfallStepRow {
  step: number;
  name: string;
  count: number;
  phase: "recruitment" | "training" | "ops";
  dropCount: number | null;
  dropPct: number | null;
  avgDaysInStage?: number | null;
}

export interface PipelineWaterfallResponse {
  steps: WaterfallStepRow[];
  totalTypeform: number;
  totalBodApproved: number;
  totalTrackingPool: number;
  totalReleasedForAssignment: number;
  periodLabel: string;
}

/**
 * Fetches GET /api/forge/pipeline-waterfall and polls every 5 min.
 * Returns pre-aggregated 31-step pipeline counts.
 */
export function usePipelineWaterfall(
  period: "all" | "ytd" | "l12m" = "all",
  intervalMs = 300_000,
) {
  const url = `/api/forge/pipeline-waterfall?period=${period}`;
  return usePolling<PipelineWaterfallResponse>(url, intervalMs);
}

// ── useDriverTraining ─────────────────────────────────────────────────────────

/**
 * Fetches GET /api/forge/driver-training and polls every 2 min.
 * Returns driver training application records ordered by training_request_for ASC.
 */
export function useDriverTraining(intervalMs = 120_000) {
  return usePolling<ForgeResponse<DriverTrainingRow>>(
    "/api/forge/driver-training",
    intervalMs,
  );
}

// ── useLosap ──────────────────────────────────────────────────────────────────

/**
 * Fetches GET /api/forge/losap and polls every 2 min.
 * Returns LOSAP (Length of Service Award Program) records by year.
 */
export function useLosap(intervalMs = 120_000) {
  return usePolling<ForgeResponse<LosapRow>>("/api/forge/losap", intervalMs);
}

// ── Persona / TAM types ───────────────────────────────────────────────────────

export type Persona = "zoe" | "michael" | "xavier" | "barbara" | "unknown";

export interface PersonaMap {
  [key: string]: number;
  zoe: number;
  michael: number;
  xavier: number;
  barbara: number;
  unknown: number;
}

// ── useTam ────────────────────────────────────────────────────────────────────

export interface TamPersonaRow {
  persona: Persona;
  label: string;
  color: string;
  tam: number;
  eligible: number;
  prospects: number;
  currentApplicants: number;
  currentMembers: number;
  newMembersThisYear?: number;
  /** marketShare = currentMembers / eligible — true market penetration, always <1 */
  marketShare?: number;
  /** annualFillRate = currentMembers / annual prospects — may exceed 1 for mature personas */
  annualFillRate?: number;
  /** annualCaptureRate = new members this year / annual prospects */
  annualCaptureRate?: number;
  /** fillRate: kept for backward compat, equals marketShare */
  fillRate: number;
}

export interface TamResponse {
  tam: TamPersonaRow[];
}

/**
 * Fetches GET /api/forge/tam and polls every 5 min.
 * Returns TAM/eligible/prospects + current pipeline fill rate per persona.
 */
export function useTam(intervalMs = 300_000) {
  return usePolling<TamResponse>("/api/forge/tam", intervalMs);
}

// ── usePersonaBreakdown ───────────────────────────────────────────────────────

export interface PersonaBreakdownStage {
  stage: string;
  personas: PersonaMap;
  total: number;
}

export interface PersonaBreakdownResponse {
  stages: PersonaBreakdownStage[];
}

/**
 * Fetches GET /api/forge/persona-breakdown and polls every 5 min.
 * Returns pipeline stage × persona distribution.
 */
export function usePersonaBreakdown(intervalMs = 300_000) {
  return usePolling<PersonaBreakdownResponse>(
    "/api/forge/persona-breakdown",
    intervalMs,
  );
}

// ── useChannelAttribution ─────────────────────────────────────────────────────

export interface ChannelAttributionRow {
  source: string;
  personas: PersonaMap;
  total: number;
  conversionRate: number;
}

export interface ChannelAttributionResponse {
  channels: ChannelAttributionRow[];
}

/**
 * Fetches GET /api/forge/channel-attribution and polls every 5 min.
 * Returns source channel → persona distribution with BOD conversion rates.
 */
export function useChannelAttribution(intervalMs = 300_000) {
  return usePolling<ChannelAttributionResponse>(
    "/api/forge/channel-attribution",
    intervalMs,
  );
}

// ── useStageDetail ────────────────────────────────────────────────────────────

export interface StageDetailResponse {
  stageNum: number;
  current: number;
  reachedStage: number;
  personas: PersonaMap;
  avgDaysInStage: number | null;
  transitionRate: number;
  lossRate: number;
  projectedWeeksToDecision: Record<
    Persona,
    { min: number; max: number } | null
  >;
}

/**
 * Fetches GET /api/forge/stage/:stageNum and polls every 5 min.
 * Returns detailed analysis for a single recruitment pipeline stage (1–9).
 */
export function useStageDetail(stageNum: number, intervalMs = 300_000) {
  const url =
    stageNum >= 1 && stageNum <= 9 ? `/api/forge/stage/${stageNum}` : null;
  return usePolling<StageDetailResponse>(url, intervalMs);
}

// ── useGeography ──────────────────────────────────────────────────────────────

export interface ZipGeoData {
  zip: string;
  city: string;
  state: string;
  lat: number | null;
  lon: number | null;
  active: number;
  separated: number;
  longTenure: number;
  totalEver: number;
  retentionRate: number;
  retentionColor: "green" | "amber" | "red" | "gray";
  tier: string;
  personas: PersonaMap;
}

export interface GeographySummary {
  totalActive: number;
  totalZips: number;
  mappableZips: number;
  avgRetentionRate: number;
  topZip: { zip: string; city: string; active: number } | null;
  highestRetention: { zip: string; city: string; retentionRate: number } | null;
  biggestOpportunity: {
    zip: string;
    city: string;
    retentionRate: number;
  } | null;
}

export interface GeographyResponse {
  zips: ZipGeoData[];
  summary: GeographySummary;
}

/**
 * Fetches GET /api/forge/geography and polls every 5 min.
 * Returns per-ZIP member counts, retention rates, and persona breakdowns.
 */
export function useGeography(intervalMs = 300_000) {
  return usePolling<GeographyResponse>("/api/forge/geography", intervalMs);
}

// ── Weekly Report types ──────────────────────────────────────────────────────

export interface CoordinatorNotes {
  id?: number;
  report_date: string;
  notes?: string | null;
  last_ais_notes?: string | null;
  next_ais_notes?: string | null;
  updated_by?: string | null;
  updated_at?: string | null;
}

export interface WeeklyNotesResponse {
  notes: CoordinatorNotes | null;
}

export interface WeeklyReportEmailMeta {
  reportDate: string;
  weekLabel: string;
  totalApplicants: number;
  attendanceRate: number;
  pendingScreening: number;
  newThisWeek: number;
  topSource: string;
  topSourceCount: number;
  lastAisDate: string | null;
  nextAisDate: string | null;
  generatedAt: string;
}

export interface WeeklyReportEmailResponse {
  subject: string;
  plainText: string;
  html: string;
  meta: WeeklyReportEmailMeta;
}

// ── useWeeklyNotes ────────────────────────────────────────────────────────────

/**
 * Fetches GET /api/forge/weekly-notes and polls every 2 min.
 * Optionally filter to a specific report_date (YYYY-MM-DD).
 */
export function useWeeklyNotes(reportDate?: string, intervalMs = 120_000) {
  const qs = reportDate ? `?report_date=${encodeURIComponent(reportDate)}` : "";
  return usePolling<WeeklyNotesResponse>(
    `/api/forge/weekly-notes${qs}`,
    intervalMs,
  );
}

// ── useWeeklyReportEmail ──────────────────────────────────────────────────────

/**
 * Fetches GET /api/forge/weekly-report-email on demand (null URL until triggered).
 * Call refresh() to re-fetch.
 */
export function useWeeklyReportEmail(reportDate?: string, intervalMs = 0) {
  const qs = reportDate ? `?report_date=${encodeURIComponent(reportDate)}` : "";
  // intervalMs = 0 means no polling — we treat 0 as "never auto-refresh"
  return usePolling<WeeklyReportEmailResponse>(
    `/api/forge/weekly-report-email${qs}`,
    intervalMs > 0 ? intervalMs : 3_600_000, // fallback large interval = effectively no polling
  );
}

// ── Benchmark types + hook ────────────────────────────────────────────────────

export type BenchmarkRating =
  | "bottom-quartile"
  | "below-average"
  | "above-average"
  | "top-quartile";

export interface BenchmarkResult {
  metric: string;
  label: string;
  dcvfdCurrent: number | null;
  nationalAverage: number;
  topQuartile: number;
  unit: "percent" | "days" | "count" | "ratio";
  direction: "higher-better" | "lower-better";
  source: string;
  note?: string;
  /** Live value from D1 aggregates (overrides dcvfdCurrent if available). */
  currentValue: number | null;
  rating: BenchmarkRating | null;
  /** Positive means better-than-national-average, negative means worse. */
  gap: number | null;
  /** True when currentValue came from live D1 data. */
  isLive: boolean;
}

export interface BenchmarksResponse {
  benchmarks: BenchmarkResult[];
}

/**
 * Fetches GET /api/forge/benchmarks and polls every 5 min.
 * Returns national VFD/EMS benchmarks with live DCVFD values where available.
 */
export function useBenchmarks(intervalMs = 300_000) {
  return usePolling<BenchmarksResponse>("/api/forge/benchmarks", intervalMs);
}
